import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Use the local logo file directly (cache-busted)
const LOGO_URL = '/logo.png?v=2';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/collections', label: t('nav.collections') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#fffef2]/95 backdrop-blur-sm border-b border-[#bcbbb4]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-28 lg:h-32">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={LOGO_URL}
              alt="Altınzade Kuyumculuk"
              className="h-20 lg:h-28 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link relative text-sm font-medium tracking-wide transition-all duration-300 ${
                  isScrolled ? 'text-[#333333]' : 'text-white'
                } ${
                  location.pathname === link.path
                    ? 'after:w-full'
                    : 'after:w-0 hover:after:w-full'
                } after:content-[''] after:absolute after:bottom-0 after:left-0 after:h-[1px] after:transition-all after:duration-500 ${
                  isScrolled ? 'after:bg-[#333333]' : 'after:bg-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Language Toggle & Mobile Menu */}
          <div className="flex items-center gap-6">
            <button
              onClick={toggleLanguage}
              className={`text-sm font-medium tracking-wider transition-all duration-300 px-3 py-1 border ${
                isScrolled
                  ? 'text-[#333333] border-[#333333] hover:bg-[#333333] hover:text-[#fffef2]'
                  : 'text-white border-white hover:bg-white hover:text-[#333333]'
              }`}
            >
              {language === 'tr' ? 'EN' : 'TR'}
            </button>

            <button
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X
                  className={`w-6 h-6 transition-colors ${
                    isScrolled ? 'text-[#333333]' : 'text-white'
                  }`}
                />
              ) : (
                <Menu
                  className={`w-6 h-6 transition-colors ${
                    isScrolled ? 'text-[#333333]' : 'text-white'
                  }`}
                />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen ? 'max-h-80 pb-6' : 'max-h-0'
          }`}
        >
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-sm font-medium tracking-wide py-2 transition-colors ${
                  isScrolled ? 'text-[#333333]' : 'text-white'
                } ${
                  location.pathname === link.path
                    ? 'opacity-100'
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
